import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ColonyDotName } from "@/lib/colonyColors";
import { toast } from "sonner";

const METHODS = ["Cash", "Check", "Zelle", "ACH", "Bank transfer", "Other"];

export const ReceivedPaymentEditDialog = ({ payment, onOpenChange, onSaved }) => {
  const [form, setForm] = useState({ amount: "", paid_at: "", payment_method: "", payment_reference: "", note: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!payment) return;
    setForm({
      amount: String(payment.amount || ""),
      paid_at: payment.paid_at || "",
      payment_method: payment.payment_method || "",
      payment_reference: payment.payment_reference || "",
      note: payment.note || "",
    });
  }, [payment]);

  const refSpec = useMemo(() => {
    const method = form.payment_method.toLowerCase();
    if (method.includes("check")) return { label: "Check #", placeholder: "1042", digitsOnly: true };
    if (method.includes("zelle")) return { label: "Zelle transaction #", placeholder: "ABC123" };
    if (method.includes("ach") || method.includes("bank")) return { label: "Confirmation #", placeholder: "TRN12345" };
    return null;
  }, [form.payment_method]);

  const save = async () => {
    const amount = Number(form.amount);
    if (!(amount > 0)) return toast.error("Enter an amount greater than zero.");
    if (!form.paid_at) return toast.error("Choose the payment date.");
    setSaving(true);
    try {
      await api.patch(`/mgmt/payments/${payment.id}`, {
        amount,
        paid_at: form.paid_at,
        payment_method: form.payment_method,
        payment_reference: refSpec ? form.payment_reference : "",
        note: form.note.trim(),
      });
      toast.success("Payment updated");
      onSaved();
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={Boolean(payment)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="received-payment-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit received payment</DialogTitle>
          <DialogDescription>Update the recorded payment details without changing its colony or linked invoice.</DialogDescription>
        </DialogHeader>
        {payment && (
          <div className="space-y-4">
            <div className="border border-border bg-secondary/25 rounded-sm px-3 py-2 text-sm flex flex-wrap items-center justify-between gap-2" data-testid="received-payment-edit-context">
              <ColonyDotName id={payment.colony_id} name={payment.colony_name || "Colony"} />
              <span className="font-semibold">{payment.invoice_number || "Manual payment"}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label htmlFor="received-payment-edit-amount">Amount</Label><Input id="received-payment-edit-amount" type="number" min="0.01" step="0.01" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} data-testid="received-payment-edit-amount" /></div>
              <div><Label htmlFor="received-payment-edit-date">Date received</Label><Input id="received-payment-edit-date" type="date" value={form.paid_at} onChange={(event) => setForm({ ...form, paid_at: event.target.value })} data-testid="received-payment-edit-date" /></div>
            </div>
            <div className={refSpec ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : ""}>
              <div>
                <Label>Paid by</Label>
                <Select value={form.payment_method || "__none__"} onValueChange={(value) => setForm({ ...form, payment_method: value === "__none__" ? "" : value, payment_reference: "" })}>
                  <SelectTrigger data-testid="received-payment-edit-method"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Not specified</SelectItem>{METHODS.map((method) => <SelectItem key={method} value={method}>{method}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {refSpec && (
                <div><Label htmlFor="received-payment-edit-reference">{refSpec.label}</Label><Input id="received-payment-edit-reference" inputMode={refSpec.digitsOnly ? "numeric" : "text"} value={form.payment_reference} placeholder={refSpec.placeholder} onChange={(event) => setForm({ ...form, payment_reference: refSpec.digitsOnly ? event.target.value.replace(/\D/g, "") : event.target.value })} data-testid="received-payment-edit-reference" /></div>
              )}
            </div>
            <div><Label htmlFor="received-payment-edit-note">Note</Label><Input id="received-payment-edit-note" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} data-testid="received-payment-edit-note" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="received-payment-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !(Number(form.amount) > 0) || !form.paid_at} data-testid="received-payment-edit-save">{saving ? "Saving…" : `Save ${fmtMoney(form.amount)}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};